import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios, { API_BASE_URL } from "../../../components/api/axios";
import ImageLoader from "../../common/ImageLoader";
import "./SingleAnnouncement.css";

function SingleAnnouncement() {
  const { eventName } = useParams();
  const navigate = useNavigate();

  const [announcement, setAnnouncement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timer, setTimer] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const fetchAnnouncement = async () => {
      setLoading(true);
      try {
        const res = await axios.get("/announcements");
        const normalizeStr = (str) =>
          (str || "").toLowerCase().replace(/[\s\-_]+/g, " ").trim();

        const target = normalizeStr(decodeURIComponent(eventName || ""));

        const found = res.data.find((a) => {
          if (!a) return false;
          if (String(a.id) === String(eventName)) return true;
          return normalizeStr(a.title) === target;
        });

        setAnnouncement(found || null);
        window.scrollTo(0, 0);
      } catch (err) {
        console.error("❌ Failed to fetch announcement:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncement();
  }, [eventName]);

  useEffect(() => {
    if (!announcement || !announcement.date) return;

    const eventDate = new Date(announcement.date).getTime();
    if (isNaN(eventDate)) return;

    const interval = setInterval(() => {
      const now = new Date().getTime();
      const diff = eventDate - now;

      if (diff <= 0) {
        clearInterval(interval);
        setTimer({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      setTimer({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [announcement]);

  if (loading) {
    return (
      <div className="announcement-detail-container loading-state">
        <div className="loading-spinner"></div>
        <p>Loading announcement details...</p>
      </div>
    );
  }

  if (!announcement) {
    return (
      <div className="announcement-detail-container not-found-state">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left"></i>
          <span>Back to Announcements</span>
        </button>
        <div className="not-found-card glass-panel">
          <i className="fas fa-search-minus"></i>
          <h3>Announcement Not Found</h3>
          <p>We couldn't locate details for this event.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="announcement-detail-container">
      <div className="detail-header-bar">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left"></i>
          <span>Back to Announcements</span>
        </button>
      </div>

      <div className="announcement-detail-wrapper">
        {/* Title and Tagline */}
        <div className="event-hero-header">
          <span className="event-badge">✦ OFFICIAL EVENT</span>
          <h1 className="event-title">{announcement.title}</h1>
          <p className="event-tagline">
            “{announcement.tagline || "Where innovation meets imagination"}”
          </p>
        </div>

        {/* Poster & Main Event Details Layout */}
        <div className="event-body-grid">
          {/* Left: Poster Image Frame */}
          <div className="event-poster-card glass-panel">
            <ImageLoader
              src={`${API_BASE_URL}/${announcement.poster}`}
              alt={announcement.title}
              className="announcement-detail-img"
            />
          </div>

          {/* Right: Info & Countdown */}
          <div className="event-details-card glass-panel">
            <h3 className="details-section-title">About the Event</h3>
            <p className="event-full-description">
              {announcement.fullDescription || announcement.description || announcement.shortDescription}
            </p>

            {/* Quick Meta Cards */}
            <div className="detail-cards-grid">
              <div className="meta-info-box">
                <i className="far fa-calendar-alt meta-icon cyan"></i>
                <div>
                  <span className="meta-label">Date</span>
                  {announcement.date ? (
                    <strong className="meta-value">{announcement.date}</strong>
                  ) : (
                    <span className="meta-value tba-badge">
                      <span className="tba-pulse-dot"></span> To Be Announced
                    </span>
                  )}
                </div>
              </div>

              <div className="meta-info-box">
                <i className="far fa-clock meta-icon blue"></i>
                <div>
                  <span className="meta-label">Time</span>
                  {announcement.time ? (
                    <strong className="meta-value">{announcement.time}</strong>
                  ) : (
                    <span className="meta-value tba-badge">
                      <span className="tba-pulse-dot"></span> To Be Announced
                    </span>
                  )}
                </div>
              </div>

              <div className="meta-info-box">
                <i className="fas fa-map-marker-alt meta-icon amber"></i>
                <div>
                  <span className="meta-label">Location</span>
                  <strong className="meta-value">{announcement.location || "DIET Campus"}</strong>
                </div>
              </div>

              <div className="meta-info-box">
                <i className="fas fa-users-cog meta-icon purple"></i>
                <div>
                  <span className="meta-label">Organized By</span>
                  <strong className="meta-value">{announcement.organizedBy || "DIET ACM Chapter"}</strong>
                </div>
              </div>
            </div>

            {/* Event Countdown */}
            {announcement.date ? (
              <div className="countdown-box">
                <h4 className="countdown-title">
                  <i className="fas fa-hourglass-half"></i> Event Countdown
                </h4>
                <div className="countdown-timer-grid">
                  <div className="timer-unit">
                    <span className="unit-number">{timer.days}</span>
                    <span className="unit-label">Days</span>
                  </div>
                  <div className="timer-unit">
                    <span className="unit-number">{timer.hours}</span>
                    <span className="unit-label">Hours</span>
                  </div>
                  <div className="timer-unit">
                    <span className="unit-number">{timer.minutes}</span>
                    <span className="unit-label">Minutes</span>
                  </div>
                  <div className="timer-unit">
                    <span className="unit-number">{timer.seconds}</span>
                    <span className="unit-label">Seconds</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="countdown-box tba-countdown-box">
                <div className="tba-countdown-content">
                  <i className="fas fa-calendar-check tba-sparkle-icon"></i>
                  <h4 className="tba-countdown-title">Schedule Coming Soon</h4>
                  <p className="tba-countdown-desc">
                    Exact date and timing details will be revealed shortly. Stay tuned!
                  </p>
                </div>
              </div>
            )}

            {/* Registration Action */}
            {announcement.registrationLink && (
              <div className="register-action-wrapper">
                <a
                  href={announcement.registrationLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="register-btn-link"
                >
                  <button className="btn-register-event">
                    <i className="fas fa-ticket-alt"></i>
                    <span>Register For Event</span>
                  </button>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SingleAnnouncement;
